import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Layout,
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Modal,
  Form,
  DatePicker,
  Progress,
  Avatar,
  Tooltip,
  Select,
  Empty,
} from "antd";
import {
  FaLayerGroup,
  FaSearch,
  FaCheckCircle,
  FaPauseCircle,
  FaEdit,
  FaPlus,
  FaPlayCircle,
  FaCalendarAlt,
  FaTasks,
  FaArrowLeft,
  FaProjectDiagram,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import Api from "../../api/Api";
import dayjs from "dayjs";
import "./Etapa.css";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDate = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return str;
  }
};

const getStatusInfo = (id_status) => {
  const map = {
    1: { label: "ABERTO", color: "blue", icon: <FaPlayCircle /> },
    2: { label: "FECHADO", color: "green", icon: <FaCheckCircle /> },
  };
  return map[id_status] || { label: "DESCONHECIDO", color: "default", icon: null };
};

const calcularDuracaoEmDias = (inicio, fim) => {
  if (!inicio || !fim) return 0;
  const dateInicio = new Date(inicio);
  const dateFim = new Date(fim);
  const diffTime = Math.abs(dateFim - dateInicio);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

// Paleta de cores predefinidas
const PALETA_CORES = [
  "#1677ff",
  "#52c41a",
  "#faad14",
  "#f5222d",
  "#722ed1",
  "#13c2c2",
  "#eb2f96",
  "#fa8c16",
  "#a0d911",
  "#2f54eb",
];

/* ─────────────────────────────────────────────────────────────────────────────
   COMPONENTE: CRONOGRAMA GANTT (MELHORADO, COM PERMISSÕES)
───────────────────────────────────────────────────────────────────────────── */
const CronogramaGantt = ({ etapas, onEdit, isDiretor }) => {

  const gerarDias = (min, max) => {
    const dias = [];
    let current = new Date(min);
    current.setHours(0, 0, 0, 0);
    const fim = new Date(max);
    fim.setHours(0, 0, 0, 0);

    while (current <= fim) {
      dias.push(new Date(current));
      current = new Date(current);
      current.setDate(current.getDate() + 1);
    }

    return dias;
  };

  const todasDatas =
    etapas && etapas.length
      ? etapas.flatMap((e) => [new Date(e.inicio), new Date(e.fim)])
      : [];

  const dataMinima = todasDatas.length ? new Date(Math.min(...todasDatas)) : null;
  const dataMaxima = todasDatas.length ? new Date(Math.max(...todasDatas)) : null;

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const diasArray = useMemo(() => {
    if (!dataMinima || !dataMaxima) return [];
    return gerarDias(dataMinima, dataMaxima);
  }, [etapas]);

  const totalDias = diasArray.length;

  if (!etapas || etapas.length === 0) {
    return (
      <Empty
        description="Nenhuma etapa cadastrada"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{ padding: "60px 0" }}
      />
    );
  }

  const isHoje = (data) => data.toDateString() === hoje.toDateString();

  // Verifica se uma etapa pode ser editada: diretor e não fechada
  const podeEditar = (etapa) => isDiretor && etapa.id_status !== 2;

  return (
    <div className="gantt-container" style={{ width: "100%", overflowX: "auto" }}>
      <div style={{ minWidth: 900 }}>
        {/* Cabeçalho com os dias */}
        <div className="gantt-header" style={{ display: "flex", borderBottom: "1px solid #f0f0f0" }}>
          <div style={{ width: 250, flexShrink: 0, padding: "8px 0", fontWeight: 600, color: "#8c8c8c" }}>
            Etapa
          </div>
          <div style={{ flex: 1, display: "flex", position: "relative" }}>
            {diasArray.map((dia, idx) => {
              const diaAtual = isHoje(dia);
              const colunaStyle = {
                flex: 1,
                textAlign: "center",
                fontSize: 11,
                padding: "6px 0",
                borderRight: idx < diasArray.length - 1 ? "1px solid #f5f5f5" : "none",
                background: diaAtual ? "rgba(22, 119, 255, 0.08)" : "transparent",
                fontWeight: diaAtual ? 700 : 400,
                color: diaAtual ? "#1677ff" : "#8c8c8c",
                position: "relative",
                minWidth: 24,
              };
              return (
                <div key={idx} style={colunaStyle}>
                  {dayjs(dia).format("DD")}
                  {diaAtual && (
                    <div
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: 0,
                        bottom: "100%",
                        width: 1,
                        borderLeft: "1.5px dashed #1677ff",
                        zIndex: 2,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Linhas das etapas */}
        {etapas.map((etapa) => {
          const inicio = new Date(etapa.inicio);
          const fim = new Date(etapa.fim);
          inicio.setHours(0, 0, 0, 0);
          fim.setHours(0, 0, 0, 0);

          const duracaoDias = calcularDuracaoEmDias(etapa.inicio, etapa.fim);
          const offsetDias = Math.ceil((inicio - dataMinima) / (1000 * 60 * 60 * 24));
          const posicaoInicio = (offsetDias / totalDias) * 100;
          const larguraBarra = (duracaoDias / totalDias) * 100;
          const statusInfo = getStatusInfo(etapa.id_status);

          return (
            <div
              key={etapa.id}
              className="gantt-row"
              style={{ display: "flex", borderBottom: "1px solid #f5f5f5", minHeight: 72 }}
            >
              {/* Lado esquerdo: informações da etapa */}
              <div style={{ width: 250, flexShrink: 0, padding: "12px 16px", background: "#fff", borderRight: "1px solid #f0f0f0" }}>
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                    <Space size="small">
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: etapa.cor || "#1677ff",
                          boxShadow: "0 0 0 2px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Text strong style={{ fontSize: 13 }}>
                        {etapa.descricao}
                      </Text>
                    </Space>
                    {podeEditar(etapa) && (
                      <Tooltip title="Editar etapa">
                        <Button
                          type="text"
                          size="small"
                          icon={<FaEdit />}
                          onClick={() => onEdit(etapa)}
                          style={{ color: "#1677ff" }}
                        />
                      </Tooltip>
                    )}
                  </Space>
                  <Space size="small" wrap>
                    <Tag color={statusInfo.color} icon={statusInfo.icon} style={{ borderRadius: 12, fontSize: 11 }}>
                      {statusInfo.label}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {formatDate(etapa.inicio)} → {formatDate(etapa.fim)} • {duracaoDias}d
                    </Text>
                  </Space>
                  <Progress
                    percent={parseFloat(etapa.progresso)}
                    size="small"
                    strokeColor={etapa.cor || "#1677ff"}
                    status={parseFloat(etapa.progresso) === 100 ? "success" : "active"}
                    style={{ marginTop: 4 }}
                  />
                </Space>
              </div>

              {/* Área do gráfico */}
              <div style={{ flex: 1, position: "relative", padding: "12px 4px" }}>
                <div style={{ position: "absolute", inset: 0, display: "flex" }}>
                  {diasArray.map((dia, idx) => {
                    const destaque = isHoje(dia);
                    return (
                      <div
                        key={idx}
                        style={{
                          flex: 1,
                          borderRight: idx < diasArray.length - 1 ? "1px solid #f5f5f5" : "none",
                          background: destaque ? "rgba(22, 119, 255, 0.04)" : "transparent",
                          minWidth: 24,
                        }}
                      />
                    );
                  })}
                </div>

                <Tooltip
                  title={
                    <>
                      <div>{etapa.descricao}</div>
                      <div>{formatDate(etapa.inicio)} - {formatDate(etapa.fim)}</div>
                      <div>Progresso: {parseFloat(etapa.progresso).toFixed(0)}%</div>
                    </>
                  }
                >
                  <div
                    style={{
                      position: "absolute",
                      left: `${posicaoInicio}%`,
                      width: `${larguraBarra}%`,
                      height: 36,
                      backgroundColor: etapa.cor || "#1677ff",
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: "pointer",
                      transition: "transform 0.3s, box-shadow 0.3s",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      zIndex: 1,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-50%) scale(1.02)";
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.25)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(-50%) scale(1)";
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
                    }}
                  >
                    {parseFloat(etapa.progresso).toFixed(0)}%
                  </div>
                </Tooltip>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE ADICIONAR/EDITAR ETAPA (inalterado)
───────────────────────────────────────────────────────────────────────────── */
const EtapaFormModal = ({ open, onClose, onSuccess, editingEtapa, projetoId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [corSelecionada, setCorSelecionada] = useState("#1677ff");

  useEffect(() => {
    if (open) {
      if (editingEtapa) {
        form.setFieldsValue({
          descricao: editingEtapa.descricao,
          inicio: editingEtapa.inicio ? dayjs(editingEtapa.inicio) : null,
          fim: editingEtapa.fim ? dayjs(editingEtapa.fim) : null,
          id_status: editingEtapa.id_status,
        });
        setCorSelecionada(editingEtapa.cor || "#1677ff");
      } else {
        form.resetFields();
        setCorSelecionada("#1677ff");
      }
    }
  }, [open, editingEtapa, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        descricao: values.descricao,
        cor: corSelecionada,
        inicio: values.inicio ? values.inicio.format("YYYY-MM-DD") : null,
        fim: values.fim ? values.fim.format("YYYY-MM-DD") : null,
      };

      if (editingEtapa) {
        await Api.AlterEtapa({
          ...payload,
          id: editingEtapa.id,
          id_status: values.id_status,
        });
        toast.success("Etapa atualizada com sucesso!");
      } else {
        await Api.AddEtapa({
          ...payload,
          id_projeto: projetoId,
        });
        toast.success("Etapa criada com sucesso!");
      }

      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        toast.error("Preencha todos os campos obrigatórios.");
      } else {
        toast.error(editingEtapa ? "Erro ao atualizar etapa." : "Erro ao criar etapa.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={600}
      title={
        <Space>
          <FaLayerGroup style={{ color: "#1677ff", fontSize: 20 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>
            {editingEtapa ? "Editar Etapa" : "Nova Etapa"}
          </span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 6 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          style={{ borderRadius: 6, fontWeight: 500 }}
        >
          {editingEtapa ? "Atualizar" : "Criar Etapa"}
        </Button>,
      ]}
      bodyStyle={{ padding: "24px 24px 0" }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Descrição</span>}
          name="descricao"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <Input placeholder="Ex: Fase de Planejamento" size="large" />
        </Form.Item>

        <Form.Item label={<span style={{ fontWeight: 500 }}>Cor da Etapa</span>}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {PALETA_CORES.map((cor) => (
                <Tooltip key={cor} title={cor}>
                  <div
                    onClick={() => setCorSelecionada(cor)}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      backgroundColor: cor,
                      cursor: "pointer",
                      border: corSelecionada === cor ? "3px solid #1677ff" : "2px solid #f0f0f0",
                      transition: "all 0.2s",
                      boxShadow: corSelecionada === cor ? "0 2px 8px rgba(22, 119, 255, 0.3)" : "none",
                    }}
                  />
                </Tooltip>
              ))}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: 12,
                borderRadius: 8,
                backgroundColor: "#f5f5f5",
              }}
            >
              <Text strong>Cor selecionada:</Text>
              <div
                style={{
                  width: 60,
                  height: 30,
                  borderRadius: 6,
                  backgroundColor: corSelecionada,
                  border: "2px solid #fff",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              />
              <Text type="secondary">{corSelecionada}</Text>
            </div>
          </Space>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Data de Início</span>}
              name="inicio"
              rules={[{ required: true, message: "Campo obrigatório" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 500 }}>Data de Fim</span>}
              name="fim"
              rules={[
                { required: true, message: "Campo obrigatório" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const inicio = getFieldValue("inicio");
                    if (!value || !inicio || value.isAfter(inicio) || value.isSame(inicio)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Fim deve ser após ou igual ao início"));
                  },
                }),
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        {editingEtapa && (
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Status</span>}
            name="id_status"
            rules={[{ required: true, message: "Campo obrigatório" }]}
          >
            <Select size="large" placeholder="Selecione o status">
              <Option value={1}>
                <Space>
                  <FaPlayCircle style={{ color: "#1677ff" }} />
                  ABERTO
                </Space>
              </Option>
              <Option value={2}>
                <Space>
                  <FaCheckCircle style={{ color: "#52c41a" }} />
                  FECHADO
                </Space>
              </Option>
            </Select>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   ETAPA PAGE
───────────────────────────────────────────────────────────────────────────── */
const Etapa = () => {
  const { id: projetoId } = useParams();
  const userStored = JSON.parse(localStorage.getItem("user")) || {};
  const isDiretor = userStored.diretor === "S";

  const [etapas, setEtapas] = useState([]);
  const [projeto, setProjeto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("TODOS");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingEtapa, setEditingEtapa] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Gestão de Etapas | Dashboard";
  }, []);

  const loadProjeto = useCallback(async () => {
    try {
      const response = await Api.ListProjeto({ id_direcao: userStored.direcao });
      const projetoEncontrado = response?.find((p) => p.id === parseInt(projetoId));
      setProjeto(projetoEncontrado || null);
    } catch {
      toast.error("Erro ao carregar projeto.");
      setProjeto(null);
    }
  }, [projetoId, userStored.direcao]);

  const loadEtapas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.ListEtapa({ id_projeto: projetoId });
      setEtapas(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar etapas.");
      setEtapas([]);
    } finally {
      setLoading(false);
    }
  }, [projetoId]);

  useEffect(() => {
    if (projetoId) {
      loadProjeto();
      loadEtapas();
    }
  }, [projetoId, loadProjeto, loadEtapas]);

  const openFormModal = (etapa = null) => {
    // Permissões: diretor sempre pode criar nova etapa
    if (!etapa) {
      if (!isDiretor) {
        toast.warning("Apenas diretores podem adicionar etapas.");
        return;
      }
    } else {
      // Edição: apenas diretores e a etapa não pode estar fechada
      if (!isDiretor) {
        toast.warning("Apenas diretores podem editar etapas.");
        return;
      }
      if (etapa.id_status === 2) {
        toast.warning("Etapas fechadas não podem ser editadas.");
        return;
      }
    }
    setEditingEtapa(etapa);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingEtapa(null);
  };

  const handleFormSuccess = () => {
    loadEtapas();
  };

  const countAberto = etapas.filter((e) => e.id_status === 1).length;
  const countFechado = etapas.filter((e) => e.id_status === 2).length;
  const totalTarefas = etapas.reduce((acc, e) => acc + (e.n_tarefa || 0), 0);
  const progressoMedio =
    etapas.length > 0
      ? etapas.reduce((acc, e) => acc + parseFloat(e.progresso || 0), 0) / etapas.length
      : 0;

  const filtered = etapas.filter((e) => {
    const statusInfo = getStatusInfo(e.id_status);
    const matchFilter = filter === "TODOS" || statusInfo.label === filter;
    const q = search.toLowerCase();
    const matchSearch = !q || e.descricao?.toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  return (
    <Layout className="etapa-layout" style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        <Button
          icon={<FaArrowLeft />}
          onClick={() => navigate("/projectos")}
          style={{ marginBottom: 24, borderRadius: 6 }}
          size="large"
        >
          Voltar aos Projetos
        </Button>

        {/* Cabeçalho */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22, 119, 255, 0.15)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaLayerGroup style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Gestão de Etapas
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}>
                    {projeto ? (
                      <>
                        <FaProjectDiagram style={{ marginRight: 8 }} />
                        Projeto: {projeto.titulo}
                      </>
                    ) : (
                      "Organize as etapas do seu projeto"
                    )}
                  </Text>
                </div>
              </Space>
            </Col>
            {/* Botão "Nova Etapa" visível apenas para diretores */}
            {isDiretor && (
              <Col>
                <Button
                  type="primary"
                  size="large"
                  icon={<FaPlus />}
                  onClick={() => openFormModal()}
                  style={{
                    backgroundColor: "#fff",
                    color: "#1677ff",
                    border: "none",
                    fontWeight: 600,
                    borderRadius: 30,
                    padding: "0 24px",
                    height: 48,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                >
                  Nova Etapa
                </Button>
              </Col>
            )}
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Total de Etapas</span>}
                value={etapas.length}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#1677ff" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#e6f4ff",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaLayerGroup style={{ fontSize: 24, color: "#1677ff" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Etapas Abertas</span>}
                value={countAberto}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#1677ff" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#e6f4ff",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaPlayCircle style={{ fontSize: 24, color: "#1677ff" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Total de Tarefas</span>}
                value={totalTarefas}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#722ed1" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#f9f0ff",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaTasks style={{ fontSize: 24, color: "#722ed1" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="stat-card"
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Progresso Médio</span>}
                value={progressoMedio.toFixed(1)}
                suffix="%"
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#52c41a" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#f6ffed",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaCheckCircle style={{ fontSize: 24, color: "#52c41a" }} />
                  </div>
                }
              />
            </Card>
          </Col>
        </Row>

        {/* Barra de ferramentas */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Input
                size="large"
                prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                placeholder="Pesquisar por descrição..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ borderRadius: 30 }}
              />
            </Col>
            <Col>
              <Select
                size="large"
                style={{ width: 160, borderRadius: 30 }}
                value={filter}
                onChange={setFilter}
              >
                <Option value="TODOS">Todos os status</Option>
                <Option value="ABERTO">Abertas</Option>
                <Option value="FECHADO">Fechadas</Option>
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Cronograma */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 24 }}
          title={
            <Space>
              <FaCalendarAlt style={{ color: "#1677ff" }} />
              <span style={{ fontWeight: 600, fontSize: 16 }}>Cronograma de Etapas</span>
            </Space>
          }
        >
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <Space direction="vertical" size="large">
                <FaLayerGroup style={{ fontSize: 48, color: "#d9d9d9" }} />
                <Text type="secondary">Carregando etapas...</Text>
              </Space>
            </div>
          ) : (
            <CronogramaGantt 
              etapas={filtered} 
              onEdit={openFormModal} 
              isDiretor={isDiretor} 
            />
          )}
        </Card>

        {/* Modal */}
        <EtapaFormModal
          open={formModalOpen}
          onClose={closeFormModal}
          onSuccess={handleFormSuccess}
          editingEtapa={editingEtapa}
          projetoId={projetoId}
        />
      </Content>
    </Layout>
  );
};

export default Etapa;